import { keyBy } from 'lodash';

import {
  AIOnboardingAssetField,
  AIOnboardingLiabilityField,
  AIOnboardingLifeInsurancePolicyField,
  AIOnboardingModalFormShapeKeys,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { mapAssetsToInput } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/assets.fields.utils';
import { mapLiabilitiesToInput } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/liabilities.fields.utils';
import { mapLifeInsurancePoliciesToInput } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/lifeInsurancePolicies.fields.utils';
import {
  AiOnboardingModal_EntityFragment,
  AiOnboardingModal_HouseholdFragment,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { getEntitySubtypeUpdateFieldForPolicy } from '@/modules/estate/estateHoldings/estateHoldings.utils';
import {
  AugmentedCreateLiabilityInput,
  AugmentedUpdateEntityInput,
  EntityStage,
  UpsertAssetToCurrentValuationInput,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

export interface HoldingOwnerEntityLookup {
  id: AiOnboardingModal_EntityFragment['id'];
  onboardingEntitySuggestion?: AiOnboardingModal_EntityFragment['onboardingEntitySuggestion'];
}

/**
 * Finds the entity ID for a given suggested owner ID of a holding (asset, liability, life insurance policy).
 * If the suggested owner ID is a valid entity ID, it returns the entity ID.
 * Otherwise, if the suggested owner ID is an AI suggestion, it checks if the AI suggestion has been created as an entity, if so, it returns the entity ID.
 * Otherwise, it returns an empty string.
 *
 * @param suggestedOwnerID
 * @param entities
 * @returns
 */
export function getHoldingOwnerEntityID(
  suggestedOwnerID: string,
  entities: HoldingOwnerEntityLookup[]
): string {
  if (!suggestedOwnerID) {
    return '';
  }

  const pulidKind = getPulidKind(suggestedOwnerID);

  if (pulidKind === PulidKind.Entity) {
    return suggestedOwnerID;
  }

  if (pulidKind === PulidKind.AiSuggestion) {
    const matchingEntity = entities.find((entity) => {
      if (!entity?.onboardingEntitySuggestion) return false;
      return entity.onboardingEntitySuggestion.some(
        (suggestion) => suggestion?.id === suggestedOwnerID
      );
    });

    if (matchingEntity) {
      return matchingEntity.id;
    }
  }

  return '';
}

/**
 * Aggregates holdings (assets, liabilities, life insurance policies) into their
 * respective mutation inputs, grouping by entity where necessary.
 *
 * Sets entities to ACTIVE stage if they receive ANY holdings (assets, liabilities, or policies).
 *
 * @param assets - Asset fields to create
 * @param liabilities - Liability fields to create (returned as flat list)
 * @param lifeInsurancePolicies - Life insurance policy fields to create (grouped by entity)
 * @param formPaths - Which form paths are active
 * @param household - Household context
 * @param suggestionsByID - Suggestions lookup
 * @returns Object containing entity updates, liability creates, and asset upserts
 */
export function getBulkCreateHoldingsInput(
  assets: AIOnboardingAssetField[],
  liabilities: AIOnboardingLiabilityField[],
  lifeInsurancePolicies: AIOnboardingLifeInsurancePolicyField[],
  formPaths: AIOnboardingModalFormShapeKeys[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): {
  updateEntitiesWithPoliciesInput: AugmentedUpdateEntityInput[];
  createLiabilitiesInput: AugmentedCreateLiabilityInput[];
  upsertAssetsInput: UpsertAssetToCurrentValuationInput[];
} {
  // Get asset upsert inputs (already grouped by entity in mapAssetsToInput)
  const upsertAssetsInput = formPaths.includes('assets')
    ? mapAssetsToInput(assets, household, suggestionsByID)
    : [];

  // Get liability create inputs (flat list, no grouping needed)
  const createLiabilitiesInput = formPaths.includes('liabilities')
    ? mapLiabilitiesToInput(liabilities, household, suggestionsByID)
    : [];

  // Pre-filter valid liabilities and policies
  const validLiabilities = formPaths.includes('liabilities')
    ? liabilities.filter(
        (liability) =>
          liability.name &&
          liability.outstandingBalance &&
          liability.recipientId
      )
    : [];

  const validPolicies = formPaths.includes('lifeInsurancePolicies')
    ? lifeInsurancePolicies.filter(
        (policy) =>
          policy.ownerId && policy.policyType && policy.deathBenefitAmount
      )
    : [];

  // Create entity lookup map
  const entities = getNodes(household.entities);
  const entityLookup = keyBy(entities, 'id');

  // Collect all entity IDs that are receiving ANY holdings
  const entitiesReceivingHoldings = new Set<string>();

  // Add entities receiving assets
  upsertAssetsInput.forEach((assetInput) => {
    entitiesReceivingHoldings.add(assetInput.entityID);
  });

  // Add entities receiving liabilities (recipientId is the debtor entity)
  validLiabilities.forEach((liability) => {
    entitiesReceivingHoldings.add(liability.recipientId);
    if (liability.issuerId) {
      entitiesReceivingHoldings.add(liability.issuerId);
    }
  });

  // Add entities receiving policies
  validPolicies.forEach((policy) => {
    entitiesReceivingHoldings.add(policy.ownerId);
  });

  // Group policies by owner entity
  const policiesByEntityId: Record<
    string,
    AIOnboardingLifeInsurancePolicyField[]
  > = {};

  validPolicies.forEach((policy) => {
    const entityId = policy.ownerId;
    if (!policiesByEntityId[entityId]) {
      policiesByEntityId[entityId] = [];
    }
    policiesByEntityId[entityId]!.push(policy);
  });

  // For each entity receiving holdings, create entity update input if needed
  const updateEntitiesWithPoliciesInput: AugmentedUpdateEntityInput[] = [];

  entitiesReceivingHoldings.forEach((entityId) => {
    const entity = entityLookup[entityId];
    if (!entity) {
      diagnostics.warn(
        `[getBulkCreateHoldingsInput] Entity not found for ID: ${entityId}`
      );
      return;
    }

    const entityPolicies = policiesByEntityId[entityId] || [];

    // Map entity's policies to create inputs (if any)
    const entityPolicyInputs =
      entityPolicies.length > 0
        ? mapLifeInsurancePoliciesToInput(
            entityPolicies,
            household,
            suggestionsByID
          )
        : [];

    const entityType = getEntityTypeFromEntityKind(entity.kind);
    const subtypeUpdateField = getEntitySubtypeUpdateFieldForPolicy(entityType);

    const needsStageActivation = entity.stage === EntityStage.PreCreated;

    const canAttachPolicies =
      entityPolicyInputs.length > 0 && Boolean(subtypeUpdateField);

    if (!needsStageActivation && !canAttachPolicies) {
      return;
    }

    // Build the entity update input
    let updateEntityInput: AugmentedUpdateEntityInput = {
      id: entityId,
      update: needsStageActivation ? { stage: EntityStage.Active } : {},
    };

    // Add policies if any (only if entity supports them)
    if (canAttachPolicies) {
      updateEntityInput = {
        ...updateEntityInput,
        [subtypeUpdateField!]: {
          id: entity.subtype.id,
          update: {},
          withPolicies: entityPolicyInputs,
        },
      };
    }

    updateEntitiesWithPoliciesInput.push(updateEntityInput);
  });

  return {
    updateEntitiesWithPoliciesInput,
    createLiabilitiesInput,
    upsertAssetsInput,
  };
}
